import React from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheck,
  FileSearch,
  WandSparkles,
  Files,
  FolderLock,
  LineChart,
  Building2,
  CheckCircle2,
} from 'lucide-react';
import { getAppHostTargets } from '../../utils/hostnamePortal';

const featureCards = [
  { title: 'Credit Report Analysis', icon: FileSearch },
  { title: 'Dispute Wizard', icon: WandSparkles },
  { title: 'Letter Packages', icon: Files },
  { title: 'Document Vault', icon: FolderLock },
  { title: 'Progress Tracking', icon: LineChart },
  { title: 'Agency Portal', icon: Building2 },
];

const platformFeatures = [
  'Smart credit report analysis',
  'Dispute wizard',
  'Bureau-specific packages',
  'Document management',
  'Round tracking',
  'Letter preview/export',
  'Agency client workspace',
  'White-label foundation',
  'MongoDB Atlas cloud database',
  'Secure portal access',
];

const steps = [
  'Import or upload your credit report',
  'Review accounts, inquiries, and personal information',
  'Select dispute items through the wizard',
  'Preview your letter package',
  'Export, send, and track progress',
];

const PublicLandingPage = () => {
  const { selfRepairHost, adminAgencyHost } = getAppHostTargets();

  return (
    <div className="min-h-screen bg-stone-100 text-stone-900">
      <header className="border-b border-stone-200/80 bg-white/80 backdrop-blur">
        <div className="mx-auto flex w-full max-w-7xl items-center justify-between px-6 py-4">
          <Link to="/" className="text-xl font-semibold tracking-tight text-stone-900">
            FundElite
          </Link>
          <nav className="hidden items-center gap-6 text-sm font-medium text-stone-700 md:flex">
            <Link to="/features" className="hover:text-emerald-700">Features</Link>
            <Link to="/pricing" className="hover:text-emerald-700">Pricing</Link>
            <Link to="/login" className="hover:text-emerald-700">Login</Link>
          </nav>
          <div className="flex items-center gap-3">
            <a
              href={adminAgencyHost}
              className="rounded-full border border-stone-300 bg-white px-4 py-2 text-sm font-medium text-stone-800 hover:bg-stone-50"
            >
              Agency Login
            </a>
            <a
              href={selfRepairHost}
              className="rounded-full bg-emerald-700 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-800"
            >
              Start Self Repair
            </a>
          </div>
        </div>
      </header>

      <main>
        <section className="relative overflow-hidden bg-gradient-to-br from-[#064E3B] via-[#1C1917] to-[#064E3B] text-white">
          <div className="absolute -left-24 top-16 h-64 w-64 rounded-full bg-[#D4AF37]/20 blur-3xl" />
          <div className="absolute -right-16 bottom-0 h-56 w-56 rounded-full bg-emerald-500/20 blur-3xl" />
          <div className="mx-auto grid w-full max-w-7xl gap-10 px-6 py-20 md:grid-cols-2 md:items-center">
            <div>
              <p className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs uppercase tracking-wide text-stone-200">
                <ShieldCheck size={14} />
                Compliance-first workflow platform
              </p>
              <h1 className="text-4xl font-semibold leading-tight md:text-5xl">
                AI-Powered Credit Repair. Built for Accuracy, Compliance, and Speed.
              </h1>
              <p className="mt-5 max-w-2xl text-lg text-stone-200">
                FundElite helps consumers and agencies organize credit reports, identify dispute opportunities, generate professional letters, manage documents, and track dispute progress from one secure portal.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href={selfRepairHost} className="rounded-full bg-[#D4AF37] px-6 py-3 font-medium text-stone-900 hover:bg-[#c89f2a]">
                  Start Self Repair
                </a>
                <a href={adminAgencyHost} className="rounded-full border border-white/30 bg-white/10 px-6 py-3 font-medium text-white hover:bg-white/20">
                  Agency Login
                </a>
              </div>
            </div>
            <div className="rounded-3xl border border-white/20 bg-white/10 p-6 shadow-2xl backdrop-blur">
              <p className="text-sm uppercase tracking-wider text-stone-300">What you get</p>
              <div className="mt-4 space-y-3">
                {['Guided dispute workflow', 'Centralized document vault', 'Role-based portal controls', 'White-label-ready architecture'].map((item) => (
                  <div key={item} className="flex items-center gap-3 rounded-xl bg-white/10 px-4 py-3">
                    <CheckCircle2 size={18} className="text-[#D4AF37]" />
                    <span className="text-stone-100">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-7xl px-6 py-16">
          <div className="grid gap-4 md:grid-cols-3">
            {featureCards.map(({ title, icon: Icon }) => (
              <div key={title} className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
                <div className="mb-4 inline-flex rounded-xl bg-emerald-50 p-3 text-emerald-700">
                  <Icon size={18} />
                </div>
                <h3 className="font-semibold text-stone-900">{title}</h3>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-white py-16">
          <div className="mx-auto w-full max-w-7xl px-6">
            <h2 className="text-3xl font-semibold text-stone-900">How It Works</h2>
            <div className="mt-8 grid gap-4 md:grid-cols-5">
              {steps.map((step, index) => (
                <div key={step} className="rounded-2xl border border-stone-200 bg-stone-50 p-4">
                  <p className="mb-2 text-sm font-semibold text-emerald-700">Step {index + 1}</p>
                  <p className="text-sm text-stone-700">{step}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto grid w-full max-w-7xl gap-6 px-6 py-16 md:grid-cols-2">
          <div className="rounded-3xl border border-stone-200 bg-white p-8 shadow-sm">
            <h3 className="text-2xl font-semibold text-stone-900">Self Repair</h3>
            <p className="mt-3 text-stone-600">
              For users who want to manage their own credit repair workflow with guided AI assistance.
            </p>
            <a href={selfRepairHost} className="mt-6 inline-flex rounded-full bg-emerald-700 px-5 py-3 text-sm font-medium text-white hover:bg-emerald-800">
              Open Self Repair Portal
            </a>
          </div>
          <div className="rounded-3xl border border-[#D4AF37]/40 bg-gradient-to-br from-stone-900 to-[#1C1917] p-8 text-stone-100 shadow-sm">
            <h3 className="text-2xl font-semibold">Agency</h3>
            <p className="mt-3 text-stone-300">
              For agencies that need client management, document organization, dispute workflows, and white-label-ready operations.
            </p>
            <a href={adminAgencyHost} className="mt-6 inline-flex rounded-full bg-[#D4AF37] px-5 py-3 text-sm font-medium text-stone-900 hover:bg-[#c89f2a]">
              Open Agency/Admin Portal
            </a>
          </div>
        </section>

        <section className="bg-stone-900 py-16 text-stone-100">
          <div className="mx-auto w-full max-w-7xl px-6">
            <h2 className="text-3xl font-semibold">Platform Features</h2>
            <div className="mt-8 grid gap-3 md:grid-cols-2">
              {platformFeatures.map((feature) => (
                <div key={feature} className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm">
                  {feature}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-7xl px-6 py-16">
          <div className="rounded-3xl border border-stone-200 bg-white p-8">
            <h3 className="text-xl font-semibold text-stone-900">Compliance Disclaimer</h3>
            <p className="mt-3 text-sm text-stone-600">
              FundElite provides software tools and workflow support. It does not guarantee credit score increases, deletions, approvals, or specific financial outcomes. Users are responsible for reviewing all letters and information before use.
            </p>
          </div>
        </section>

        <section className="bg-gradient-to-r from-[#064E3B] to-[#1C1917] py-16 text-white">
          <div className="mx-auto w-full max-w-5xl px-6 text-center">
            <h2 className="text-3xl font-semibold md:text-4xl">Ready to organize your credit repair workflow?</h2>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <a href={selfRepairHost} className="rounded-full bg-[#D4AF37] px-6 py-3 font-medium text-stone-900 hover:bg-[#c89f2a]">
                Start Self Repair
              </a>
              <a href={adminAgencyHost} className="rounded-full border border-white/30 bg-white/10 px-6 py-3 font-medium text-white hover:bg-white/20">
                Agency Login
              </a>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default PublicLandingPage;
